import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ClientConfigOptions } from '../../clientConfig';
import { ApiResponse } from '../types';
import { rsaSign } from '../../utils/signer';

/**
 * HTTP请求封装类
 * 提供基于axios的HTTP请求方法，自动处理请求拦截、响应处理和错误处理
 * @class Request
 */
export class Request {
   /** 客户端配置选项 */
   private config: ClientConfigOptions;
   /** Axios实例 */
   private service: AxiosInstance;

   /**
    * 创建请求实例
    * @param {ClientConfigOptions} config 
    */
   constructor(config: ClientConfigOptions) {
      this.config = {
         timeout: 10000,
         ...config
      };

      this.service = axios.create({
         baseURL: this.config.baseUrl,
         timeout: this.config.timeout,
         headers: {
            'Content-Type': 'application/json',
            ...(this.config.headers || {})
         }
      });

      this.service.interceptors.request.use(
         this._handleRequestIntercept.bind(this),
         (error: AxiosError) => Promise.reject(error)
      );

      this.service.interceptors.response.use(
         this._handleResponseSuccess.bind(this),
         this._handleResponseError.bind(this)
      );
   }

   /**
    * 处理请求拦截器
    * 在请求发送前自动添加业务ID和应用ID
    * @param {InternalAxiosRequestConfig} config - Axios请求配置
    * @returns {InternalAxiosRequestConfig} 修改后的请求配置
    * @private
    */
   _handleRequestIntercept(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
      const { businessId, applicationId, privateKeys } = this.config;

      const hasIdentifiers = Boolean(businessId || applicationId);
      if (!hasIdentifiers) {
         return config;
      }

      const commonParams = {
         ...(businessId ? { businessId } : {}),
         ...(applicationId ? { applicationId } : {})
      };
      const methodHandlers = {
         get: () => {
            config.params = {
               ...(config.params || {}),
               ...commonParams
            };
         },
         default: () => {
            if (!config.data) {
               return
            }

            const data = typeof config.data === 'string' ? JSON.parse(config.data) : config.data;

            const mergeData = {
               ...commonParams,
               ...data
            }
            config.data = {
               ...mergeData,
               sign: rsaSign(mergeData, privateKeys)
            };
         }
      };

      // 修复类型错误
      const method = (config.method?.toLowerCase() || 'default') as keyof typeof methodHandlers;
      const handler = methodHandlers[method] || methodHandlers.default;
      handler();

      console.log('完整请求参数:', JSON.stringify(config, null, 2));

      return config;
   }

   /**
    * 处理响应成功
    * 统一处理响应格式
    * @param {AxiosResponse} response - Axios响应对象
    * @returns {AxiosResponse} 处理后的响应
    * @private
    */
   _handleResponseSuccess(response: AxiosResponse): AxiosResponse {
      // 如果响应数据已经符合ApiResponse格式，直接返回
      if (response.data && 
          (response.data.hasOwnProperty('success') || 
           response.data.hasOwnProperty('code'))) {
         // 确保响应格式统一
         const apiResponse: ApiResponse = {
            success: response.data.success !== undefined ? response.data.success : (response.data.code === 200),
            message: response.data.message || '',
            code: response.data.code || (response.data.success ? 200 : 500),
            result: response.data.result || response.data.data,
            timestamp: response.data.timestamp || Date.now()
         };
         response.data = apiResponse;
      } else {
         // 如果响应不符合格式，包装成标准格式
         const apiResponse: ApiResponse = {
            success: true,
            message: '操作成功！',
            code: 200,
            result: response.data,
            timestamp: Date.now()
         };
         response.data = apiResponse;
      }
      return response;
   }

   /**
    * 发送GET请求
    * @template T - 响应数据类型
    * @param {string} path - 请求路径
    * @param {Record<string, any>} params - URL查询参数
    * @param {AxiosRequestConfig} options - 额外的请求配置选项
    * @returns {Promise<ApiResponse<T>>} 响应数据
    */
   async get<T = any>(path: string, params: Record<string, any> = {}, options: AxiosRequestConfig = {}): Promise<ApiResponse<T>> {
      try {
         const response = await this.service.get(path, {
            params,
            ...options
         });
         return response.data;
      } catch (error) {
         throw error;
      }
   }

   /**
    * 发送POST请求
    * @template T - 响应数据类型
    * @param {string} path - 请求路径
    * @param {Record<string, any>} data - 请求体数据
    * @param {AxiosRequestConfig} options - 额外的请求配置选项
    * @returns {Promise<ApiResponse<T>>} 响应数据
    */
   async post<T = any>(path: string, data: Record<string, any> = {}, options: AxiosRequestConfig = {}): Promise<ApiResponse<T>> {
      try {
         const response = await this.service.post(path, data, options);
         return response.data;
      } catch (error) {
         throw error;
      }
   }

   /**
    * 处理响应错误
    * 根据不同的错误类型和状态码提供适当的错误处理
    * @param {AxiosError} error - Axios错误对象
    * @returns {Promise<never>} 返回被拒绝的Promise
    * @private
    */
   _handleResponseError(error: AxiosError): Promise<never> {
      if (error.response) {
         // 服务器返回了错误状态码
         const { status, data } = error.response;
         const errorMsg = (data as any)?.message || JSON.stringify(data);
         
         // 构造标准错误响应
         const errorResponse: ApiResponse = {
            success: false,
            message: errorMsg,
            code: status,
            result: null,
            timestamp: Date.now()
         };

         // 可以根据状态码进行不同处理
         switch (status) {
            case 401:
               // 处理未授权错误
               console.error('授权失败，请重新登录');
               break;
            case 403:
               // 处理禁止访问错误
               console.error('没有权限访问该资源');
               break;
            case 404:
               // 处理资源不存在错误
               console.error('请求的资源不存在');
               break;
            case 500:
               // 处理服务器错误
               console.error('服务器内部错误');
               break;
            default:
               // 处理其他错误
               console.error(`API请求失败: ${status} - ${errorMsg}`);
         }

         return Promise.reject(errorResponse);
      } else if (error.request) {
         // 请求已发送但没有收到响应
         console.error('API无响应，请检查网络连接');
         
         const errorResponse: ApiResponse = {
            success: false,
            message: `API无响应: ${error.message}`,
            code: 0,
            result: null,
            timestamp: Date.now()
         };
         
         return Promise.reject(errorResponse);
      } else {
         // 请求设置时发生错误
         console.error(`请求配置错误: ${error.message}`);
         
         const errorResponse: ApiResponse = {
            success: false,
            message: `请求错误: ${error.message}`,
            code: 0,
            result: null,
            timestamp: Date.now()
         };
         
         return Promise.reject(errorResponse);
      }
   }
} 